import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {MSpinner, MCard, MGridLayout} from "components/Shared";
import {getMCardConfiguration, getMCardData, updateMCardData} from "requests/customerConfiguration";
import {insertValueFieldIntoCards} from "selectors/homeowner/caseForms/caseHeaderV2";
import responseHandler from "responseHandler";
import AppraisalRequirements from "components/Customer/SurveyTypes/AppraisalRequirements.tsx";

// From Mueller.Business.BusinessLogic.CustomerSurveyBusinessLogic
export const INFO_CARD_KEY = "complete-form-surveyTypeCard";
export const BILLING_CARD_KEY = "complete-form-billingCard";
export const REQUIREMENTS_CARD_KEY = "complete-form-requirementsCard"; 

const CARD_LAYOUT = [
  // Row 1: Survey Type Information, Service Types, Field Settings
  [
    { key: "complete-form-surveyTypeCard", size: 4 },
    { key: "complete-form-serviceTypeCard", size: 4 },
    { key: "complete-form-fieldSettingsCard", size: 4 },
  ],
  // Row 2: QA Queue Rules, QA ITV Rules, Appraisal Settings
  [
    { key: "complete-form-qaQueueRulesCard", size: 4 },
    { key: "complete-form-qaQueueItvRulesCard", size: 4 },
    { key: "complete-form-appraisalSettingsCard", size: 4 },
  ],
  // Row 3: Billing, Billing Apportionment
  [
    { key: "complete-form-billingCard", size: 6 },
    { key: "complete-form-billingApportionmentCard", size: 6 },
  ],
  // Row 4: Requirements (full width)
  [
    { key: "complete-form-requirementsCard", size: 12 },
  ],
];

const CustomerSurveyTypeConfiguration = (props) => {
  const [data, setData] = useState(null);
  const [mCardConfiguration, setMCardConfiguration] = useState(null);
  const [mCardConfigurationApplied, setMCardConfigurationApplied] = useState(null);
  const [isBusy, setIsBusy] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    return () => mounted.current = false;
  }, []);

  useEffect(() => {
    setIsBusy(true);
    setData(null);
    setMCardConfiguration(null);
    getMCardData(props.customerNumber, props.surveyTypeCode, setData, mounted);
    getMCardConfiguration(props.customerNumber, props.surveyTypeCode, setMCardConfiguration, mounted);
  }, [props.customerNumber, props.surveyTypeCode]);

  useEffect(() => {
    if (data && mCardConfiguration) {
      setIsBusy(true);
      let cardsAndData = {
        cards: global.structuredClone(mCardConfiguration),
        caseHeaderDto: data,
      };
      insertValueFieldIntoCards(cardsAndData);
      setMCardConfigurationApplied(cardsAndData.cards);
      setIsBusy(false);
    }
  }, [data, mCardConfiguration]);

  const handleSave = (updatedData) => {
    let dictData = {};
    // transform to an IDictionary structure for the web call
    updatedData.forEach((d) => {
      if (typeof (d.value) === "undefined") {
        dictData[d.key] = null;
      } else {
        dictData[d.key] = d.value;
      }
    });
    setIsBusy(true);
    // throw error when only updating FieldServiceAutoQaStartDate when !EnableFieldServicesAutoQa 
    let throwErrorFSQA = (dictData.hasOwnProperty("FieldServiceAutoQaStartDate") && !dictData.hasOwnProperty("EnableFieldServicesAutoQa") && !data.enableFieldServicesAutoQa);
    if (throwErrorFSQA) {
      props.makeToastError("Field Services Auto QA must be set to Yes to set Field Services Auto QA Start Date.");
      setIsBusy(false);
      return;
    }
    updateMCardData(props.customerNumber, props.surveyTypeCode, dictData, setData, null, mounted);
  };

  if (!mCardConfiguration || !data || !mCardConfigurationApplied || isBusy) {
    return <MSpinner/>;
  }

  // Create a map for quick lookup of card config by key
  const cardConfigMap = {};
  mCardConfigurationApplied.forEach(c => {
    cardConfigMap[c.key] = c;
  });

  // Render card by key
  const renderCard = (cardKey) => {
    const c = cardConfigMap[cardKey];
    if (!c) return null;
    return (
      <MCard
        {...c}
        key={c.key}
        onEditFinished={handleSave}
        children={c.key === REQUIREMENTS_CARD_KEY 
          ? <AppraisalRequirements />
          : null}
      />
    );
  };

  // Get all keys from CARD_LAYOUT for comparison
  const layoutKeys = new Set(CARD_LAYOUT.flat().map(item => item.key));
  
  // Find cards that aren't in the layout (for fallback rendering)
  const unmappedCards = mCardConfigurationApplied.filter(c => !layoutKeys.has(c.key));

  // Build rows for MGridLayout based on CARD_LAYOUT
  const rows = CARD_LAYOUT.map(row => 
    row
      .filter(({ key }) => cardConfigMap[key]) // Only include cards that exist in config
      .map(({ key, size }) => ({
        component: renderCard(key),
        size,
      }))
  ).filter(row => row.length > 0); // Remove empty rows

  // Add unmapped cards as full-width rows at the end
  unmappedCards.forEach(c => {
    rows.push([{ component: renderCard(c.key), size: 12 }]);
  });

  return (
    <MGridLayout
      verticalDisplay={props.verticalDisplay}
      rows={rows}
    />
  );
};

CustomerSurveyTypeConfiguration.propTypes = {
  customerNumber: PropTypes.number,
  surveyTypeCode: PropTypes.string,
  verticalDisplay: PropTypes.bool,
  makeToastError: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  makeToastError: text => {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(null, mapDispatchToProps)(CustomerSurveyTypeConfiguration);
